import { Typography } from "antd";
import classNames from "classnames";
import dynamic from "next/dynamic";
import { usePathname } from "next/navigation";
import { useState } from "react";
import UserAlerts from "rivals/components/app/Layout/Header/AccountLinks/UserAlerts";
import headerStrings from "rivals/components/app/Layout/Header/strings";
import CustomButton from "rivals/components/shared/CustomButton";
import Link from "rivals/components/shared/Link/index";
import useGetCurrentUser from "rivals/services/getCurrentUser/useGetCurrentUser";
import { gaSagaEvent } from "rivals/shared/analytics";
import { CUSTOM_EVENTS } from "rivals/shared/analytics/customEvents";
import { LOGIN_TEXT, PATHS } from "rivals/shared/constants";
import { setData } from "rivals/shared/utils/qa";
import styles from "./AccountLinks.module.scss";
import type { Props } from "./types";

// TODO: https://griosf.atlassian.net/browse/RVLS-9445
const { subscribeText, upsellText } = headerStrings;

export const DATA_PHILTER = "account-links";

/**
 * UserDropdown should not be server-side rendered because it contains user
 * specific data that should not be cached.
 */
const UserDropdown = dynamic(() => import("./UserDropdown"), { ssr: false });

// StoreProvider is only used to include Redux for the SearchOverlay component
const StoreProvider = dynamic(() => import("rivals/app/StoreProvider"), {
  ssr: false
});

/**
 * There is no need to server-side render the SearchOverlay component
 * We will only include it if a user triggers a search
 */
const SearchOverlay = dynamic(
  () => import("rivals/components/app/Layout/SearchOverlay"),
  { ssr: false }
);

export default function AccountLinks({
  logout,
  site
}: Props): React.JSX.Element | null {
  const { data, isLoading } = useGetCurrentUser();

  const authenticated = !!data?.user;

  const subscribed =
    authenticated &&
    data?.user?.subscriptions &&
    data?.user?.subscriptions.length > 0;

  const [showSearchOverlay, setShowSearchOverlay] = useState(false);

  const pathname = usePathname();

  const onSearchButtonClick = (): void => {
    setShowSearchOverlay(!showSearchOverlay);
  };

  const onSubscribeLinkClick = (): void => {
    if (pathname !== PATHS.SIGN_UP) {
      gaSagaEvent(CUSTOM_EVENTS.clickNavigation(PATHS.SIGN_UP, subscribeText));
    }
  };

  const onLoginLinkClick = (): void => {
    if (pathname !== PATHS.SIGN_IN) {
      gaSagaEvent(CUSTOM_EVENTS.clickNavigation(PATHS.SIGN_IN, LOGIN_TEXT));
    }
  };

  const loginLink = (
    <Link
      className={styles.loginLink}
      href={PATHS.SIGN_IN}
      onClick={onLoginLinkClick}
      {...setData(DATA_PHILTER, "login-button")}
    >
      {LOGIN_TEXT}
    </Link>
  );

  const authenticatedLinks = data?.user && (
    <UserDropdown logoutUser={logout} site={site} user={data?.user} />
  );

  const subscribeLink = (
    <>
      <div className={styles.subscribeSection}>
        <Typography>
          <span className={styles.subscribePrompt}>{upsellText}</span>
        </Typography>
      </div>
      <div className={styles.subscribeSection}>
        <Link
          href={PATHS.SIGN_UP}
          onClick={onSubscribeLinkClick}
          {...setData(DATA_PHILTER, "subscribe-button")}
        >
          <div className={styles.subscribeButton}>{subscribeText}</div>
        </Link>
      </div>
    </>
  );

  if (isLoading) {
    return null;
  }

  return (
    <div className={styles.accountLinkMenu} {...setData(DATA_PHILTER)}>
      {!subscribed && !authenticated && subscribeLink}
      <CustomButton
        ariaLabel="search toggle"
        className={classNames(styles.searchIcon, {
          [styles.clicked]: showSearchOverlay
        })}
        onClick={onSearchButtonClick}
        philter="toggle-prospect-search-overlay"
      />
      {showSearchOverlay && (
        <StoreProvider>
          <SearchOverlay />
        </StoreProvider>
      )}
      {authenticated ? authenticatedLinks : loginLink}
      {site?.enableUserAlerts && authenticated && <UserAlerts />}
    </div>
  );
}
